import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import translate from '../../commonComponents/translate';
import Title from '../../commonComponents/Title';
import ContentContainer from '../../commonComponents/pageLayout/ContentContainer';
import GameTypeProp from './propTypes/gameTypeProp';
import GameProp from './propTypes/gameProp';
import ChallengesSection from './list/ChallengesSection';
import JourneyBanner from '../academy/list/journeyBanner/JourneyBanner';
import JourneyProp from '../journey/propTypes/journeyProp';
import {
  GAME_TYPES,
  GAME_TYPE,
  JOURNEY_STATUS,
} from '../../services/utilityFunctions';
import SubHeader from '../../commonComponents/pageLayout/SubHeader';
import Carousel from '../../commonComponents/carousel/Carousel';
import EnrolledJourney from '../academy/details/EnrolledJourney';
import TopicBanner from '../../commonComponents/pageLayout/header/TopicBanner';
import ServicesContext from '../../contextProviders/ServicesContext';
import LoggedUserContext from '../../contextProviders/LoggedUserContext';
import { ENROLL_SOURCE } from '../../services/googleAnalytics';

/**
 * User profile main page
 */
function Perform({ t, games, journeys, gameTypes }) {
  const { loggedUser } = useContext(LoggedUserContext);
  const services = useContext(ServicesContext);
  const disabledTypes =
    services &&
    services.perform &&
    services.perform.config &&
    services.perform.config.disabled
      ? services.perform.config.disabled
      : [];

  const typeNames = GAME_TYPES.filter(
    gt => gt !== GAME_TYPE.PROFILING && !disabledTypes.includes(gt),
  );
  const { unstarted, other } = journeys.reduce(
    (acc, j) =>
      j.status === JOURNEY_STATUS.UN_STARTED
        ? { other: acc.other, unstarted: acc.unstarted.concat(j) }
        : { other: acc.other.concat(j), unstarted: acc.unstarted },
    { unstarted: [], other: [] },
  );
  const hasStartedJourneys = other.length > 0;

  const initial = typeNames.reduce((acc, type) => ({ ...acc, [type]: [] }), {});
  const gamesByType = games.reduce((acc, g) => {
    if (!g.gameType || !g.gameType.type || !acc[g.gameType.type]) {
      return acc;
    }
    const { type } = g.gameType;
    return { ...acc, [type]: [...acc[type], g] };
  }, initial);

  const renderJourneyBanner = index => {
    const journey = unstarted[index % journeys.length];
    return journey ? (
      <JourneyBanner
        t={t}
        journey={journey}
        isLogged={!!loggedUser}
        source={ENROLL_SOURCE.PERFORM}
        services={services}
      />
    ) : null;
  };

  return (
    <Title title={t('perform_head_title')}>
      <ContentContainer>
        <SubHeader title={t('perform_title')} />
        <TopicBanner />
        <div className="m-content">
          <div className="full-page-container-with-subheader">
            {hasStartedJourneys && (
              <>
                <div className="academy-enrolled-subtitle">
                  <h3 className="academy-enrolled-subtitle-text">
                    {t('perform_journeys')}
                  </h3>
                </div>
                <Carousel>
                  {other.map(journey => (
                    <EnrolledJourney key={journey._id} journey={journey} />
                  ))}
                </Carousel>
                {renderJourneyBanner(0)}
                <div className="academy-subtitle">
                  <h3 className="academy-subtitle-title">
                    {t('perform_challenges')}
                  </h3>
                </div>
              </>
            )}
            {typeNames.map((type, index) => (
              <React.Fragment key={type}>
                <ChallengesSection
                  carouselId={`challenge-carousel-${type}`}
                  type={(gameTypes.find(gt => gt.type === type) || {})._id}
                  title={t(`challengeDetails_type_${type}`)}
                  games={gamesByType[type]}
                />
                {renderJourneyBanner(hasStartedJourneys ? index + 1 : index)}
              </React.Fragment>
            ))}
          </div>
        </div>
      </ContentContainer>
    </Title>
  );
}

Perform.propTypes = {
  t: PropTypes.func.isRequired,
  gameTypes: PropTypes.arrayOf(PropTypes.shape(GameTypeProp.propType))
    .isRequired,
  games: PropTypes.arrayOf(PropTypes.shape(GameProp.propType)).isRequired,
  journeys: PropTypes.arrayOf(PropTypes.shape(JourneyProp.propType)).isRequired,
};

export default translate(Perform);
